/**
 * CodeMirror extension that highlights GitHub-style alerts in the source view:
 *
 *   > [!NOTE]      → header line (bold, accent color, accent border)
 *   > content      → body line  (subtle accent border + tinted background)
 *   > [!WARNING]
 *   > ...
 *
 * Five GFM alert types are recognized: NOTE, TIP, IMPORTANT, WARNING, CAUTION.
 * Lines outside an alert (regular blockquotes) are unaffected.
 *
 * A fold service is registered so users can collapse an alert via the fold
 * gutter or Ctrl/Cmd+Shift+[ . Folding is on-demand only — alerts are NOT
 * auto-folded on document load.
 */

import { foldService } from "@codemirror/language";
import { type Extension, RangeSetBuilder } from "@codemirror/state";
import {
    Decoration,
    type DecorationSet,
    EditorView,
    ViewPlugin,
    type ViewUpdate,
} from "@codemirror/view";

const ALERT_HEADER_RE =
    /^(\s*)>\s*\[!(NOTE|TIP|IMPORTANT|WARNING|CAUTION)\]\s*$/i;
const QUOTE_LINE_RE = /^(\s*)>\s?/;

type AlertKind = "note" | "tip" | "important" | "warning" | "caution";

function lineKind(text: string): AlertKind | null {
    const m = ALERT_HEADER_RE.exec(text);
    if (!m) return null;
    return m[2].toLowerCase() as AlertKind;
}

/** Find the last continuation line of an alert that starts at `headerLineNo`. */
function findAlertEndLine(
    doc: { lines: number; line(n: number): { text: string } },
    headerLineNo: number,
): number {
    let last = headerLineNo;
    for (let i = headerLineNo + 1; i <= doc.lines; i++) {
        const t = doc.line(i).text;
        if (!QUOTE_LINE_RE.test(t)) break;
        if (lineKind(t)) break;
        last = i;
    }
    return last;
}

function buildAlertDecorations(view: EditorView): DecorationSet {
    const builder = new RangeSetBuilder<Decoration>();
    const doc = view.state.doc;
    const total = doc.lines;

    for (let lineNo = 1; lineNo <= total; lineNo++) {
        const line = doc.line(lineNo);
        const kind = lineKind(line.text);
        if (!kind) continue;

        builder.add(
            line.from,
            line.from,
            Decoration.line({
                class: `cm-gh-alert cm-gh-alert-${kind} cm-gh-alert-head`,
            }),
        );

        let next = lineNo + 1;
        while (next <= total) {
            const cont = doc.line(next);
            if (!QUOTE_LINE_RE.test(cont.text)) break;
            if (lineKind(cont.text)) break;
            builder.add(
                cont.from,
                cont.from,
                Decoration.line({
                    class: `cm-gh-alert cm-gh-alert-${kind} cm-gh-alert-body`,
                }),
            );
            next++;
        }
        lineNo = next - 1;
    }

    return builder.finish();
}

const alertViewPlugin = ViewPlugin.fromClass(
    class {
        decorations: DecorationSet;
        constructor(view: EditorView) {
            this.decorations = buildAlertDecorations(view);
        }
        update(update: ViewUpdate) {
            if (update.docChanged || update.viewportChanged) {
                this.decorations = buildAlertDecorations(update.view);
            }
        }
    },
    { decorations: (v) => v.decorations },
);

/**
 * Fold service: when the user clicks the fold gutter on an alert header line,
 * collapse from end-of-header to end-of-last-body line.
 */
const alertFoldService = foldService.of((state, lineStart) => {
    const line = state.doc.lineAt(lineStart);
    if (!lineKind(line.text)) return null;
    const endLineNo = findAlertEndLine(state.doc, line.number);
    if (endLineNo === line.number) return null;
    return { from: line.to, to: state.doc.line(endLineNo).to };
});

const alertTheme = EditorView.baseTheme({
    ".cm-gh-alert": {
        paddingLeft: "8px",
        borderLeft: "3px solid var(--gh-alert-border, transparent)",
        backgroundColor:
            "color-mix(in srgb, var(--gh-alert-border, transparent) 7%, transparent)",
    },
    ".cm-gh-alert-head": {
        fontWeight: "600",
        color: "var(--gh-alert-fg, inherit)",
        letterSpacing: "0.01em",
    },

    /* Map alert kinds to Magic MD semantic tokens (auto theme-aware) */
    ".cm-gh-alert-note": {
        "--gh-alert-fg": "var(--color-info)",
        "--gh-alert-border": "var(--color-info)",
    },
    ".cm-gh-alert-tip": {
        "--gh-alert-fg": "var(--color-success)",
        "--gh-alert-border": "var(--color-success)",
    },
    ".cm-gh-alert-important": {
        "--gh-alert-fg": "var(--cm-content-accent)",
        "--gh-alert-border": "var(--cm-content-accent)",
    },
    ".cm-gh-alert-warning": {
        "--gh-alert-fg": "var(--color-warning)",
        "--gh-alert-border": "var(--color-warning)",
    },
    ".cm-gh-alert-caution": {
        "--gh-alert-fg": "var(--color-destructive)",
        "--gh-alert-border": "var(--color-destructive)",
    },
});

export function githubAlertHighlight(): Extension[] {
    return [alertViewPlugin, alertFoldService, alertTheme];
}
